import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { map, take, tap } from 'rxjs';
import { selectActiveWorkspaceId } from 'src/app/core/reducers';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';

import { AppState } from 'src/app/reducers';
import { Dashboard } from 'src/app/core/models/core.models';
import { FormMode, FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralDynamicFormComponent } from '../../general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { NomLanguages } from 'src/app/core/constants';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Validators } from '@angular/forms';
import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-dashboard-settings',
  templateUrl: './dashboard-settings.component.html',
  styleUrls: ['./dashboard-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DashboardSettingsComponent extends GeneralDynamicFormComponent {
  @Input() dashboard: Dashboard;
  @Output() componentDataChange = new EventEmitter<{
    type: string;
    value: any;
  }>();

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    protected translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  // Declared above `formProperties`: that array is a field initializer and reads
  // this service while building the workspace picker's options.
  private userWorkspaces = inject(UserWorkspacesService);

  public activeFormProperties: FormProperty[] = [];
  public formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.DEFAULT]: 'title',
        [NomLanguages.BULGARIAN]: 'title_i18n.' + NomLanguages.BULGARIAN,
        [NomLanguages.ENGLISH]: 'title_i18n.' + NomLanguages.ENGLISH,
        [NomLanguages.ARABIC]: 'title_i18n.' + NomLanguages.ARABIC,
      },
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.dashboard-title',
      ),
      controlType: 'text',
      validators: [Validators.required],
      additionalConfig: {
        placeholder: this.translateService.instant(
          'dashboard-settings.placeholder.enter-the-dashboard-title',
        ),
      },
    },
    {
      propertyName: 'workspace.id',
      validators: [Validators.required],
      propertyLabel: 'Workspace',
      controlType: 'select',
      valueOptionsAsync: this.userWorkspaces.sortedWorkspaces$().pipe(
        map((workspaces) =>
          workspaces.map((workspace) => ({
            value: workspace.id,
            name: workspace.title,
          })),
        ),
      ),
    },
    {
      propertyName: 'description',
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.dashboard-description',
      ),
      controlType: 'textarea',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'dashboard-settings.placeholder.dashboard-description',
        ),
        rows: 4,
      },
    },
    {
      propertyName: 'default_period_in_days',
      validators: [Validators.required],
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.default-period-in-days',
      ),
      defaultValue: 7,
      controlType: 'input-number',
    },
    {
      // wire key DashboardPATCH will bind as $imageUrl (base64 -> ClientImageStorage,
      // pending the backend branch that WorkspacePATCH/FeedPATCH already have);
      // also keys the defaultImageUrl mapping in ngOnInit
      propertyName: 'image_url',
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.image',
      ),
      controlType: 'image-cropper',
      availableInFormModes: [FormMode.Edit],
      additionalConfig: {
        height: 64,
        width: 64,
        defaultImageUrl: '/assets/img/dashboard.png',
        containerStyleProperties: {
          width: '3rem',
        },
      },
    },
    {
      propertyName: 'is_auto_refresh',
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.auto-refresh',
      ),
      controlType: 'checkbox',
      formChanger: true,
    },
    {
      propertyName: 'auto_refresh_seconds',
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.auto-refresh-seconds',
      ),
      controlType: 'input-number',
      shouldDisplay: (formGroup) => {
        return formGroup.get('is_auto_refresh')?.value;
      },
      additionalConfig: {
        min: 15,
      },
    },
    {
      propertyName: 'cache_enabled',
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.enable-caching',
      ),
      controlType: 'checkbox',
      defaultValue: true,
    },
  ];

  ngOnInit() {
    if (this.dashboard) {
      this.formMode = FormMode.Edit;

      // * before super.ngOnInit(): that call snapshots formProperties into
      // * activeFormProperties (the template binding), so a later remap never renders
      this.formProperties = this.formProperties.map((prop) => {
        if (prop.propertyName !== 'image_url') return prop;

        const defaultImageUrl =
          this.dashboard.thumbnail?.length > 0
            ? this.dashboard.thumbnail
            : '/assets/img/dashboard.png';

        return {
          ...prop,
          additionalConfig: {
            ...prop.additionalConfig,
            defaultImageUrl,
          },
        };
      });
    }
    super.ngOnInit();

    if (this.dashboard) {
      this.formService.populateFormValues(this.formGroup, this.dashboard);
    } else {
      this.formGroupChange.emit(this.formGroup);

      this.store
        .select(selectActiveWorkspaceId)
        .pipe(
          take(1),
          tap((workspaceId) => {
            this.formService.populateFormValues(this.formGroup, {
              'workspace.id': workspaceId,
            });
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.formGroup
      .get('title')
      ?.valueChanges.pipe(
        tap((title) => {
          this.componentDataChange.emit({
            type: 'title',
            value: title,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
